import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Enable dayjs plugins for timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Timezone Conversion Utilities
 *
 * All dates from server are in UTC format.
 * All dates sent to server should be converted to UTC.
 * All dates displayed to user should be in their local timezone.
 */

/**
 * Convert UTC date string from server to user's local timezone for display
 * @param utcDateString - ISO date string in UTC from server
 * @returns Formatted date string in user's local timezone
 */
export const formatDateFromServer = (utcDateString: string): string => {
  return dayjs.utc(utcDateString).local().format('DD.MM.YYYY HH:mm');
};

/**
 * Convert UTC date string from server to user's local timezone (short format)
 * @param utcDateString - ISO date string in UTC from server
 * @returns Short formatted date string in user's local timezone
 */
export const formatDateFromServerShort = (utcDateString: string): string => {
  return dayjs.utc(utcDateString).local().format('DD.MM HH:mm');
};

/**
 * Convert UTC date string from server to dayjs object in user's local timezone
 * @param utcDateString - ISO date string in UTC from server
 * @returns dayjs object in user's local timezone
 */
export const parseDateFromServer = (utcDateString: string): dayjs.Dayjs => {
  return dayjs.utc(utcDateString).local();
};

/**
 * Convert local date/time to UTC for sending to server (without timezone suffix)
 * Spring Boot expects format: 2024-11-05T10:30:00 (no Z or +00:00)
 * @param localDate - dayjs object or Date in user's local timezone
 * @returns UTC datetime string without timezone suffix for Spring Boot
 */
export const formatDateForServer = (localDate: dayjs.Dayjs | Date): string => {
  if (localDate instanceof Date) {
    return dayjs(localDate).utc().format('YYYY-MM-DDTHH:mm:ss');
  }
  return localDate.utc().format('YYYY-MM-DDTHH:mm:ss');
};

/**
 * Convert local date/time from DateTimePicker to UTC for server (without timezone suffix)
 * Spring Boot expects format: 2024-11-05T10:30:00 (no Z or +00:00)
 * @param pickerValue - Dayjs value from DateTimePicker (in local timezone)
 * @returns UTC datetime string without timezone suffix for Spring Boot
 */
export const formatPickerDateForServer = (pickerValue: dayjs.Dayjs | null): string | undefined => {
  if (!pickerValue) return undefined;
  return pickerValue.utc().format('YYYY-MM-DDTHH:mm:ss');
};

/**
 * Календарный день из DatePicker для параметров формата `YYYY-MM-DD` (`format: date` в Swagger).
 * Без перевода в UTC: день — тот, что выбран в пикере, иначе у восточных поясов он съезжает на вчера.
 * @param pickerValue - Dayjs value from DatePicker
 * @returns `YYYY-MM-DD` or undefined for an empty / invalid value
 */
export const formatPickerDayForServer = (pickerValue: dayjs.Dayjs | null): string | undefined => {
  if (!pickerValue || !pickerValue.isValid()) return undefined;
  return pickerValue.format('YYYY-MM-DD');
};

/**
 * День с сервера (`YYYY-MM-DD`, `format: date` в Swagger) → `ДД.ММ.ГГГГ`.
 * Строку только переставляем: у даты без времени нет пояса, и через Date/dayjs у западных поясов
 * день съехал бы на вчера. Неожиданный формат возвращаем как есть.
 */
export const formatServerDay = (day: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : day;
};

/**
 * Convert date string from game DB (Berlin time, UTC+1/+2) to user's local timezone for display.
 * Used for data from L2 game DB where dates are stored in CET/CEST, not UTC.
 * @param berlinDateString - Date string in Berlin timezone (CET/CEST)
 * @returns Formatted date string in user's local timezone
 */
export const formatDateFromBerlin = (berlinDateString: string): string => {
  return dayjs.tz(berlinDateString, 'Europe/Berlin').local().format('DD.MM.YYYY HH:mm');
};

/**
 * Get current date/time in user's local timezone
 * @returns dayjs object in user's local timezone
 */
export const getCurrentLocalTime = (): dayjs.Dayjs => {
  return dayjs();
};

/**
 * Get current date/time in UTC for server operations (without timezone suffix)
 * @returns UTC datetime string without timezone suffix for Spring Boot
 */
export const getCurrentUTCTime = (): string => {
  return dayjs.utc().format('YYYY-MM-DDTHH:mm:ss');
};

/**
 * Legacy formatDate function replacement
 * Converts UTC date string to localized display format
 * @param utcDateString - ISO date string in UTC from server
 * @returns Formatted date string using browser's locale
 */
export const formatDateLegacy = (utcDateString: string): string => {
  return dayjs.utc(utcDateString).local().toDate().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Legacy short formatDate function replacement
 * @param utcDateString - ISO date string in UTC from server
 * @returns Short formatted date string using browser's locale
 */
export const formatDateLegacyShort = (utcDateString: string): string => {
  return dayjs.utc(utcDateString).local().toDate().toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Validate that a date is in the future (local timezone)
 * @param date - dayjs object to validate
 * @returns true if date is in the future
 */
export const isFutureDate = (date: dayjs.Dayjs): boolean => {
  return date.isAfter(dayjs());
};

/**
 * Validate that endDate is after startDate
 * @param startDate - start date in local timezone
 * @param endDate - end date in local timezone
 * @returns true if endDate is after startDate
 */
export const isDateAfter = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs): boolean => {
  return endDate.isAfter(startDate);
};

/**
 * MIGRATION HELPER: Replace dayjs().toISOString() calls
 * Creates a dayjs object and formats it for Spring Boot (UTC, no timezone suffix)
 * @param addDays - Optional number of days to add
 * @returns UTC datetime string without timezone suffix
 */
export const createUTCDateForServer = (addDays?: number): string => {
  let date = getCurrentLocalTime();
  if (addDays) {
    date = date.add(addDays, 'day');
  }
  return formatDateForServer(date);
};